/**
 * entitlement-errors — maps the SILL-521 M2 cluster 4 backend Tier-0 denials
 * to the toast + "Upgrade" CTA surface. Per the three-surface upsell rule
 * (locked tiles, event-moments, digest links, plus the post-exhaustion AI
 * CTA), a denied bulk mutation or a contact-cap denial is an EVENT MOMENT —
 * it gets a toast with an Upgrade action, never a new modal or banner.
 *
 * Wire shapes (see backend/internal/api/professional_{clients,contacts,
 * imports,crm_entitlements}.go):
 *   403 {"code":"crm_entitlement_required","capability":"bulk"|"client_detail"}
 *   403 {"code":"contact_cap_exceeded","cap":N}
 *   429 {"code":"send_limit_reached","class","limit","plan","resets_at","upgrade"}
 *       (SEC-06: a manual 1:1 email or invite resend over the daily budget)
 *
 * `capability:"book_import"` (POST /invites/book-import, Pro only) is also a
 * toast, with its own copy.
 *
 * `capability:"client_detail"` is handled by the locked-tile state
 * (the locked arm of `components/people/FinancialReadiness.tsx`), NOT a toast — this helper
 * only reacts to `capability:"bulk"`.
 */
import type { ApiError } from "./api";
import type { CaptureEvent } from "./capture-event";
import { isSendClass, sendLimitCopy, type SendLimitBody } from "./send-limits-display";

export interface EntitlementToastAction {
  label: string;
  onClick: () => void;
}

export interface EntitlementToastInput {
  title: string;
  description?: string;
  action?: EntitlementToastAction;
}

/** Structural subset of `useToast().pushToast` — avoids a tight coupling to
 *  the toast component's internal `ToastItem` type. */
export type PushEntitlementToast = (toast: EntitlementToastInput) => void;

/** Structural subset of react-router's `useNavigate()` return. */
export type NavigateFn = (path: string) => void;

export const BILLING_SETTINGS_ROUTE = "/settings#billing";

const DEFAULT_CONTACT_CAP = 25;

function upgradeAction(navigate: NavigateFn): EntitlementToastAction {
  return { label: "Upgrade", onClick: () => navigate(BILLING_SETTINGS_ROUTE) };
}

function capabilityOf(error: ApiError): string | undefined {
  const capability = error.body?.capability;
  return typeof capability === "string" ? capability : undefined;
}

function capOf(error: ApiError): number {
  const cap = error.body?.cap;
  return typeof cap === "number" ? cap : DEFAULT_CONTACT_CAP;
}

function toastForError(
  error: ApiError,
  navigate: NavigateFn,
): EntitlementToastInput | null {
  switch (error.code) {
    case "crm_entitlement_required":
      // Only the bulk-capability denial is a toast; client_detail renders
      // LockedClientDetail instead (a locked tile, not an event-moment toast).
      if (capabilityOf(error) === "book_import") {
        // SEC-06 paced "Invite the people you imported" is Pro only.
        return {
          title: "Inviting the people you imported requires Sill Pro.",
          description:
            "Upgrade to invite your whole book, paced over a few days.",
          action: upgradeAction(navigate),
        };
      }
      if (capabilityOf(error) !== "bulk") return null;
      return {
        title: "Bulk tools require Sill Pro.",
        description:
          "Upgrade for group invites, campaigns, automations, and templates.",
        action: upgradeAction(navigate),
      };
    case "contact_cap_exceeded":
      return {
        title: `Free includes ${capOf(error)} people — you're at the cap.`,
        description:
          "Archive people to free up space, or upgrade for unlimited.",
        action: upgradeAction(navigate),
      };
    case "send_limit_reached": {
      // Free (upgrade:true) gets the Upgrade CTA; Pro gets a plain reset note.
      const copy = sendLimitCopy((error.body ?? {}) as SendLimitBody);
      return {
        title: copy.title,
        description: copy.description,
        action: copy.upgrade ? upgradeAction(navigate) : undefined,
      };
    }
    default:
      return null;
  }
}

const SEND_LIMIT_PLANS = new Set(["free", "pro"]);

/**
 * `send_limit_toast_shown {class, plan}` — allowlisted props only (both are
 * validated enums; no limits, ids or timestamps).
 */
export function sendLimitToastEvent(error: ApiError): CaptureEvent {
  const cls = error.body?.class;
  const plan = error.body?.plan;
  return {
    event: "send_limit_toast_shown",
    props: {
      class: isSendClass(cls) ? cls : "unknown",
      plan: typeof plan === "string" && SEND_LIMIT_PLANS.has(plan) ? plan : "unknown",
    },
  };
}

/**
 * Maps a caught mutation error to its entitlement-denial toast when it's one
 * of the known codes above, pushing it via `pushToast`. Returns whether it
 * handled the error — the caller should suppress its own generic error
 * message/banner when true, since the toast is the sole surfacing for these
 * denials (three-surface rule: no duplicate banner).
 *
 * `capture` (optional) receives the `send_limit_toast_shown` analytics event
 * when the handled error is a `send_limit_reached`.
 */
export function crmEntitlementToast(
  error: unknown,
  pushToast: PushEntitlementToast,
  navigate: NavigateFn,
  capture?: (event: CaptureEvent) => void,
): boolean {
  const apiError = error as ApiError | undefined;
  if (!apiError || typeof apiError.code !== "string") return false;
  const toast = toastForError(apiError, navigate);
  if (!toast) return false;
  pushToast(toast);
  if (apiError.code === "send_limit_reached") capture?.(sendLimitToastEvent(apiError));
  return true;
}
